import { Filter, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { ContactUrgency } from '@/services/api/generated/treatmentContactQueueService';

const URGENCY_OPTIONS: ContactUrgency[] = [
  'Escalate to Attorney',
  'Urgent',
  'Overdue',
  'Routine',
];

export interface ContactQueueFiltersState {
  urgency: ContactUrgency[];
  daysOverdueMin: number | null;
  requiresEscalation: boolean;
}

interface ContactQueueFiltersProps {
  filters: ContactQueueFiltersState;
  onFiltersChange: (filters: ContactQueueFiltersState) => void;
}

export const ContactQueueFilters = ({
  filters,
  onFiltersChange,
}: ContactQueueFiltersProps) => {
  const activeFilterCount =
    filters.urgency.length +
    (filters.daysOverdueMin ? 1 : 0) +
    (filters.requiresEscalation ? 1 : 0);

  const handleUrgencyToggle = (urgency: ContactUrgency) => {
    const newUrgency = filters.urgency.includes(urgency)
      ? filters.urgency.filter((u) => u !== urgency)
      : [...filters.urgency, urgency];
    onFiltersChange({ ...filters, urgency: newUrgency });
  };

  const handleClearFilters = () => {
    onFiltersChange({
      urgency: [],
      daysOverdueMin: null,
      requiresEscalation: false,
    });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="h-5 min-w-5 px-1.5">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Filters</h4>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="h-auto p-0 text-xs text-zinc-500 hover:text-zinc-700"
              >
                <X className="h-3 w-3 mr-1" />
                Clear all
              </Button>
            )}
          </div>

          {/* Urgency filters */}
          <div className="space-y-2">
            <Label className="text-xs text-zinc-500 uppercase tracking-wide">
              Urgency Level
            </Label>
            <div className="space-y-2">
              {URGENCY_OPTIONS.map((urgency) => (
                <div key={urgency} className="flex items-center space-x-2">
                  <Checkbox
                    id={`urgency-${urgency}`}
                    checked={filters.urgency.includes(urgency)}
                    onCheckedChange={() => handleUrgencyToggle(urgency)}
                  />
                  <Label
                    htmlFor={`urgency-${urgency}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {urgency}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Days overdue minimum */}
          <div className="space-y-2">
            <Label
              htmlFor="daysOverdue"
              className="text-xs text-zinc-500 uppercase tracking-wide"
            >
              Minimum Days Since Contact
            </Label>
            <Input
              id="daysOverdue"
              type="number"
              min={0}
              placeholder="Any"
              value={filters.daysOverdueMin ?? ''}
              onChange={(e) =>
                onFiltersChange({
                  ...filters,
                  daysOverdueMin: e.target.value
                    ? parseInt(e.target.value, 10)
                    : null,
                })
              }
              className="h-8"
            />
          </div>

          {/* Escalation only */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="escalationOnly"
              checked={filters.requiresEscalation}
              onCheckedChange={(checked) =>
                onFiltersChange({
                  ...filters,
                  requiresEscalation: checked === true,
                })
              }
            />
            <Label
              htmlFor="escalationOnly"
              className="text-sm font-normal cursor-pointer"
            >
              Requires attorney escalation only
            </Label>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
